/**
 * UATRolePicker — Premium Prashasta UAT entry screen.
 *
 * SEC-ADMIN-01: Privileged roles (TENANT_ADMIN, BUSINESS_OWNER, CRM_MANAGER,
 * FINANCE_APPROVER) require a server-verified PIN before session issuance.
 * PIN modal intercepts role card click, calls POST /api/auth/uat-pin-verify,
 * and on success passes the one-time step_up_token to uatRoleLogin.
 * PIN is cleared from component state on every close/submit.
 *
 * Branding hierarchy (locked):
 *   1. Prashasta logo mark
 *   2. Prashasta Event Intelligence Platform  (brand.productName)
 *   3. AI Powered by ARGOV Technologies  (brand.companyName)
 *   4. One Event. One Live Truth. Every Party Connected.  (brand.primaryTagline)
 *   5. UAT TEST MODE badge  (burgundy)
 *   6. Choose your CRM role  (instruction)
 *   7. CRM role cards  (warm ivory + gold active state)
 *
 * Visual: Warm ivory background, elevated white card, gold accents.
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { ShieldAlert, ShieldCheck, ChevronRight, LogIn, AlertTriangle, Lock, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLE_HOME = {
  BUSINESS_OWNER:         "/home",
  CRM_MANAGER:            "/home",
  RELATIONSHIP_MANAGER:   "/engage/leads",   // P-ARC-01R: replaces SALES_CRM_USER
  PROJECT_MANAGER:        "/home",            // P-ARC-01R: event planning focus
  TENANT_ADMIN:           "/administration",
  EVENT_PLANNER_DESIGNER: "/planner/cases",
  // CRM-NAV-AUDIT-01: explicit home routes for new roles
  SALES_CRM_USER:         "/engage/leads",   // CRM pipeline focus
  FINANCE_APPROVER:       "/events",          // Event-scoped approval context
};

// ── Brand logo ────────────────────────────────────────────────────────────────
function BrandMark() {
  return (
    <div className="mb-6 flex items-center justify-center" data-testid="uat-tenant-logo">
      <img
        src="/prashasta-logo.png"
        alt="Prashasta Events"
        style={{ height: '52px', width: 'auto', objectFit: 'contain' }}
      />
    </div>
  );
}

// ── Role card ─────────────────────────────────────────────────────────────────
function RoleCard({ role, onLogin, loading, isPrivileged }) {
  const isLoading = loading === role.role_code;
  const [hovered, setHovered] = useState(false);

  return (
    <button
      data-testid={`uat-role-card-${role.role_code.toLowerCase().replace(/_/g, "-")}`}
      onClick={() => onLogin(role.role_code)}
      disabled={!!loading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative w-full text-left p-4 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
      style={{
        background: isLoading
          ? "linear-gradient(135deg, #FDF8F1 0%, #F7EFE4 100%)"
          : hovered
          ? "linear-gradient(135deg, #FDFAF5 0%, #F9F4EC 100%)"
          : "#FFFFFF",
        border: isLoading
          ? "1px solid var(--brand-gold)"
          : hovered
          ? "1px solid rgba(197,160,89,0.5)"
          : "1px solid var(--brand-border)",
        boxShadow: isLoading
          ? "0 0 20px rgba(197,160,89,0.15)"
          : hovered
          ? "0 4px 16px -2px rgba(28,25,23,0.06)"
          : "none",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold truncate" style={{ color: "#1C1917" }}>{role.label}</p>
            {isPrivileged && (
              <span
                data-testid={`pin-required-badge-${role.role_code.toLowerCase().replace(/_/g, "-")}`}
                className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ background: "rgba(91,29,40,0.08)", color: "var(--brand-burgundy)" }}
              >
                <Lock className="w-2 h-2" /> PIN
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5 leading-relaxed line-clamp-2" style={{ color: "#78716C" }}>
            {role.description}
          </p>
        </div>
        <div
          className="flex-shrink-0 mt-0.5 transition-transform duration-150"
          style={{ color: isLoading ? "var(--brand-gold)" : hovered ? "var(--brand-gold)" : "#D6CEBF" }}
        >
          {isLoading ? (
            <div
              className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: "var(--brand-gold)", borderTopColor: "transparent" }}
            />
          ) : (
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          )}
        </div>
      </div>
      {/* Status badge */}
      <div
        className="mt-2.5 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors duration-150"
        style={{
          background: isLoading
            ? "var(--brand-gold)"
            : hovered
            ? "var(--brand-gold-light)"
            : "#F4F0EA",
          color: isLoading ? "#FFFFFF" : "var(--brand-gold)",
        }}
      >
        <LogIn className="w-2.5 h-2.5" />
        {isLoading ? "Signing in…" : role.button_label}
      </div>
    </button>
  );
}

// ── PIN Verification Modal (SEC-ADMIN-01) ──────────────────────────────────────
function PinVerificationModal({ roleLabel, roleCode, onSuccess, onCancel }) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const inputRef = useRef(null);

  // Focus PIN input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
    // Clear PIN on unmount (security hygiene)
    return () => setPin("");
  }, []);

  const handleVerify = async () => {
    if (!pin.trim()) { setError("Please enter your PIN"); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/uat-pin-verify`, {
        role_code: roleCode,
        pin,
      });
      setPin(""); // clear immediately on success
      onSuccess(data.step_up_token);
    } catch (err) {
      setPin(""); // always clear PIN on failure
      const detail = err?.response?.data?.detail || "Verification failed. Please try again.";
      const status = err?.response?.status;
      if (status === 429) {
        setError(detail);
        setAttemptsLeft(0);
      } else if (status === 401) {
        const newLeft = Math.max(0, attemptsLeft - 1);
        setAttemptsLeft(newLeft);
        setError(newLeft === 0 ? "Too many attempts. Please wait 15 minutes." : `Incorrect PIN. ${newLeft} attempt${newLeft !== 1 ? "s" : ""} remaining.`);
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading && attemptsLeft > 0) handleVerify();
    if (e.key === "Escape") { setPin(""); onCancel(); }
  };

  const isLocked = attemptsLeft === 0;

  return (
    <div
      data-testid="pin-verification-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(28,25,23,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl p-6"
        style={{
          background: "#FFFFFF",
          border: "1px solid var(--brand-border)",
          boxShadow: "0 24px 60px -8px rgba(28,25,23,0.2)",
        }}
      >
        {/* Close */}
        <button
          data-testid="pin-modal-cancel"
          onClick={() => { setPin(""); onCancel(); }}
          className="absolute top-4 right-4 p-1 rounded-lg transition-colors"
          style={{ color: "#78716C" }}
          onMouseEnter={e => e.currentTarget.style.background = "#F4F0EA"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(91,29,40,0.08)" }}
          >
            <Lock className="w-5 h-5" style={{ color: "var(--brand-burgundy)" }} />
          </div>
          <div>
            <p className="text-xs font-bold tracking-widest uppercase" style={{ color: "var(--brand-burgundy)" }}>
              Security Verification
            </p>
            <p className="text-sm font-semibold mt-0.5" style={{ color: "#1C1917" }}>
              {roleLabel}
            </p>
          </div>
        </div>

        <p className="text-xs mb-4" style={{ color: "#78716C" }}>
          This role requires PIN verification. Enter the administrator PIN to continue.
        </p>

        {/* PIN Input */}
        <div className="relative">
          <input
            ref={inputRef}
            data-testid="pin-input"
            type={showPin ? "text" : "password"}
            value={pin}
            onChange={e => { setPin(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown}
            disabled={loading || isLocked}
            placeholder="Enter PIN"
            maxLength={12}
            autoComplete="off"
            className="w-full px-4 py-3 rounded-xl text-sm font-mono tracking-widest border outline-none transition-all"
            style={{
              border: error ? "1px solid #B91C1C" : "1px solid var(--brand-border)",
              background: isLocked ? "#FAF7F2" : "#FFFFFF",
              color: "#1C1917",
              letterSpacing: pin && !showPin ? "0.3em" : "normal",
            }}
          />
          <button
            type="button"
            onClick={() => setShowPin(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            style={{ color: "#78716C" }}
            tabIndex={-1}
          >
            {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div
            data-testid="pin-error-message"
            className="mt-2 flex items-start gap-1.5 text-xs"
            style={{ color: "#B91C1C" }}
          >
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Attempt indicator */}
        {attemptsLeft < 5 && attemptsLeft > 0 && (
          <div className="mt-1.5 flex gap-1" data-testid="pin-attempts-indicator">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-1 flex-1 rounded-full"
                style={{ background: i < attemptsLeft ? "var(--brand-gold)" : "#E5E0D8" }}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-5">
          <button
            data-testid="pin-modal-submit"
            onClick={handleVerify}
            disabled={loading || isLocked || !pin.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: isLocked ? "#E5E0D8" : "var(--brand-gold)",
              color: isLocked ? "#78716C" : "#1C1917",
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#1C1917", borderTopColor: "transparent" }} />
                Verifying…
              </span>
            ) : isLocked ? "Locked" : "Verify PIN"}
          </button>
          <button
            data-testid="pin-modal-cancel-btn"
            onClick={() => { setPin(""); onCancel(); }}
            disabled={loading}
            className="px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{ background: "#F4F0EA", color: "#57534E" }}
          >
            Cancel
          </button>
        </div>

        <p className="text-center text-[10px] mt-4" style={{ color: "#C8C0B5" }}>
          PIN values are never logged or transmitted in error responses.
        </p>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function UATRolePicker() {
  const { uatRoleLogin, uatConfig } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  // SEC-ADMIN-01: PIN modal state
  const [pinModal, setPinModal] = useState(null); // { role_code, role_label } | null

  // PRASHASTA-AUTH-01: UAT role picker retired from normal login journey.
  // Redirect users to the standard email+password login.
  // UAT testing facilities remain accessible via direct API calls for automation.
  return <Navigate to="/login" replace />;

  /* eslint-disable no-unreachable */
  const privilegedRoles = uatConfig?.privileged_roles || [];
  const pinEnabled = uatConfig?.pin_verification_enabled === true;

  // Auth config still loading
  if (uatConfig === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAF7F2" }}>
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--brand-gold)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  // Auth config failed
  if (uatConfig?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#FAF7F2" }}>
        <div
          className="bg-white rounded-2xl p-8 max-w-sm w-full text-center"
          style={{ border: "1px solid var(--brand-border)", boxShadow: "0 12px 36px -4px rgba(28,25,23,0.08)" }}
        >
          <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: "#B91C1C" }} />
          <h2 className="text-base font-semibold mb-2" style={{ color: "#1C1917" }}>Authentication Unavailable</h2>
          <p className="text-sm" style={{ color: "#78716C" }}>Auth configuration could not be loaded. Please refresh.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 rounded-full text-sm font-semibold btn-gold-shimmer"
            style={{ color: "#1C1917" }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Not in UAT mode → standard login
  if (!uatConfig?.enabled) {
    return <Navigate to="/login" replace />;
  }

  const groups = uatConfig?.groups || [];

  const handleLogin = async (role_code) => {
    setError("");
    // SEC-ADMIN-01: intercept privileged roles — open PIN modal instead of direct login
    if (pinEnabled && privilegedRoles.includes(role_code)) {
      const allRoles = groups.flatMap(g => g.roles);
      const roleObj = allRoles.find(r => r.role_code === role_code);
      setPinModal({ role_code, role_label: roleObj?.label || role_code });
      return;
    }
    // Non-privileged: direct one-click login
    setLoading(role_code);
    try {
      const user = await uatRoleLogin(role_code);
      const home = ROLE_HOME[user.role] || ROLE_HOME[role_code] || "/home";
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const handlePinSuccess = async (step_up_token) => {
    const role_code = pinModal.role_code;
    setPinModal(null);
    setLoading(role_code);
    try {
      const user = await uatRoleLogin(role_code, step_up_token);
      const home = ROLE_HOME[user.role] || ROLE_HOME[role_code] || "/home";
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed after PIN verification. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{ background: "var(--brand-ivory)" }}
    >
      {/* SEC-ADMIN-01: PIN verification modal (overlays everything) */}
      {pinModal && (
        <PinVerificationModal
          roleLabel={pinModal.role_label}
          roleCode={pinModal.role_code}
          onSuccess={handlePinSuccess}
          onCancel={() => setPinModal(null)}
        />
      )}

      {/* Subtle warm grain overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 50%, rgba(197,160,89,0.07) 0%, transparent 70%), radial-gradient(ellipse at 80% 20%, rgba(91,29,40,0.05) 0%, transparent 60%)",
        }}
      />

      <div className="relative w-full max-w-lg">
        {/* Back to landing */}
        <button
          onClick={() => navigate("/")}
          className="mb-6 flex items-center gap-1.5 text-xs transition-colors"
          style={{ color: "#78716C" }}
          onMouseEnter={e => { e.currentTarget.style.color = "var(--brand-gold)"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#78716C"; }}
          data-testid="uat-back-to-landing"
        >
          ← Back to Platform
        </button>

        {/* ── Branding header ─────────────────────────────────────────────── */}
        <div className="flex flex-col items-center mb-8 text-center">
          {/* Brand mark */}
          <BrandMark />

          {/* Platform name */}
          <h1
            data-testid="uat-picker-product-name"
            className="font-display text-2xl sm:text-3xl tracking-tight"
            style={{ color: "#1C1917" }}
          >
            {brand.productName}
          </h1>

          {/* Powered by */}
          <p
            data-testid="uat-picker-company"
            className="text-sm mt-1.5"
            style={{ color: "#78716C" }}
          >
            Powered by{" "}
            <span className="font-semibold" style={{ color: "#1C1917" }}>
              {brand.companyName} AI Intelligence
            </span>
          </p>

          {/* Tagline */}
          <p
            data-testid="uat-picker-tagline"
            className="text-xs mt-2 font-medium"
            style={{ color: "#57534E" }}
          >
            {brand.primaryTagline}
          </p>

          {/* UAT TEST MODE badge — burgundy */}
          <div
            data-testid="uat-mode-badge"
            className="mt-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
            style={{
              borderColor: "rgba(91,29,40,0.25)",
              background: "rgba(91,29,40,0.06)",
            }}
          >
            <ShieldAlert className="w-3.5 h-3.5" style={{ color: "var(--brand-burgundy)" }} />
            <span
              className="text-[11px] font-bold tracking-widest uppercase"
              style={{ color: "var(--brand-burgundy)" }}
            >
              UAT Test Mode
            </span>
          </div>

          {/* Instruction */}
          <p
            data-testid="uat-picker-instruction"
            className="text-sm font-medium mt-4"
            style={{ color: "#1C1917" }}
          >
            Choose your CRM role
          </p>
          <p className="text-xs mt-0.5" style={{ color: "#78716C" }}>
            {pinEnabled ? "Some privileged roles require a PIN" : "Click once to enter — no password required"}
          </p>
        </div>

        {/* ── Role cards ─────────────────────────────────────────────────── */}
        {groups.length === 0 ? (
          <div className="text-center text-sm py-12" style={{ color: "#78716C" }}>
            Loading roles…
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.group_label}>
                <p
                  className="text-[10px] font-bold tracking-widest uppercase mb-2 px-1"
                  style={{ color: "#78716C" }}
                >
                  {group.group_label}
                </p>
                <div
                  className={`grid gap-2 ${
                    group.roles.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
                  }`}
                >
                  {group.roles.map((role) => (
                    <RoleCard
                      key={role.role_code}
                      role={role}
                      onLogin={handleLogin}
                      loading={loading}
                      isPrivileged={pinEnabled && privilegedRoles.includes(role.role_code)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            data-testid="uat-picker-error"
            className="mt-4 flex items-center gap-2 p-3 rounded-xl text-sm"
            style={{
              background: "#FFF0F0",
              border: "1px solid rgba(185,28,28,0.2)",
              color: "#B91C1C",
            }}
          >
            <ShieldAlert className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Footer note */}
        <p
          className="text-center text-[10px] mt-8"
          style={{ color: "#D6CEBF" }}
        >
          UAT test sessions are audited. Use test identities only — do not enter real customer data.
        </p>
        <p
          className="text-center text-[9px] mt-2"
          style={{ color: "#C8C0B5", letterSpacing: '0.05em' }}
          data-testid="argov-attribution-uat"
        >
          Powered by ARGOV Technologies AI Intelligence
        </p>
      </div>
    </div>
  );
}
